import { useState, useEffect, useCallback } from 'react';
import { getProcessedSleeperData } from '../utils/sleeperIntegration';
import { displayDstName } from '../utils/teams';
import { filterPlayersWithSummary } from '../utils/playerValue';
import { validatePlayerForRanking } from '../utils/valuation/factors';
import { calculateEnhancedRankings, type EnhancedRankingConfig } from '../utils/enhancedPlayerRankingSimple';
import { PlayerFeedbackStorage } from '../utils/playerFeedbackStorage';
import { fetchAllPlayers } from '../utils/sleeperApi';
import { playerImageUrl } from '../utils/playerAssets';
import { nflSeasonContext } from '../utils/nflSeason';
import { applyLeagueInsights, type ScoringFormat } from '../utils/leagueInsights';
import { useProjections } from './useProjections';
import type { Player } from '../types/player';
import type { LeagueConfig } from '../types/league';

export interface SharedPlayerDataConfig {
  leagueConfig: LeagueConfig;
  scaleToTen?: boolean;
  scoringFormat?: ScoringFormat;
}

export interface SharedPlayerDataResult {
  players: Player[];
  isLoading: boolean;
  error: string | null;
}

export function useSharedPlayerData(config: SharedPlayerDataConfig): SharedPlayerDataResult {
  const { leagueConfig, scaleToTen = false, scoringFormat = 'standard' } = config;

  const [players, setPlayers] = useState<Player[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const {
    projections: generatedProjections,
    loading: projectionsLoading,
  } = useProjections();

  const fetchAndProcessPlayers = useCallback(async () => {
    if (projectionsLoading) {
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const { lastCompleted } = nflSeasonContext();
      const sleeperPlayersData = await fetchAllPlayers();
      if (!sleeperPlayersData || Object.keys(sleeperPlayersData).length === 0) {
        throw new Error('Sleeper player list is empty. Try again in a moment.');
      }

      const allSleeperPlayers = Object.values(sleeperPlayersData).filter((p: any) =>
        p && p.position && p.player_id && (p.full_name || p.position === 'DEF') && p.active !== false
      );

      const processed = await getProcessedSleeperData(lastCompleted);
      const { statsMap, projections: sleeperProjections, playerStatsMap, teamStatsMap, leagueAverages } = processed;

      const defaultLeagueAverages = {
        playsPerGame: 65,
        pointsPerGame: 22.5,
        redZoneScoreRate: 0.57,
      };
      const finalLeagueAverages = leagueAverages || defaultLeagueAverages;

      const validPlayers = filterPlayersWithSummary(allSleeperPlayers as any[], 'SharedPlayerData', true);
      const qualityFiltered = validPlayers.filter((player) =>
        validatePlayerForRanking(player, generatedProjections)
      );
      const fantasyPlayers = qualityFiltered.length > 40 ? qualityFiltered : validPlayers;

      const playerArr: Player[] = fantasyPlayers.map((basePlayer: any): Player => {
        const playerName =
          basePlayer.full_name ??
          (basePlayer.position === 'DEF'
            ? displayDstName(basePlayer.team)
            : `${basePlayer.first_name || ''} ${basePlayer.last_name || ''}`.trim());

        const espnId = basePlayer.espn_id ? String(basePlayer.espn_id) : undefined;
        const sleeperId = String(basePlayer.player_id);
        const imageUrl = playerImageUrl(playerName, basePlayer.position, basePlayer.team, espnId, sleeperId);
        const ageToUse =
          basePlayer.position === 'DEF'
            ? (basePlayer.age ?? 0)
            : (typeof basePlayer.age === 'number' ? basePlayer.age : 25);

        const seasonStatsData = statsMap.get(String(basePlayer.player_id));
        const lastYearPoints =
          seasonStatsData?.fpts_ppr ?? seasonStatsData?.fpts_half_ppr ?? seasonStatsData?.fpts_std ?? 0;

        const sleeperProjection = sleeperProjections[String(basePlayer.player_id)] ?? 0;
        const generated = generatedProjections[String(basePlayer.player_id)] ?? 0;
        const projection = generated > 0 ? generated : (sleeperProjection > 0 ? sleeperProjection : lastYearPoints);

        const currentPStats = playerStatsMap.get(String(basePlayer.player_id)) || {
          targets: 0,
          carries: 0,
          redZoneOpportunities: 0,
          airYards: 0,
          schedule: [],
        };

        return {
          ...basePlayer,
          id: String(basePlayer.player_id),
          espn_id: espnId,
          name: playerName,
          rank: 0,
          value: projection,
          change: 0,
          imageUrl,
          projection,
          projectedPoints: projection,
          age: ageToUse,
          lastYearPoints,
          team: basePlayer.team || '',
          position: basePlayer.position,
          byeWeek: basePlayer.bye_week || 0,
          adp: basePlayer.search_rank || basePlayer.adp || 999,
          playerStats: currentPStats,
          teamStats: teamStatsMap.get(basePlayer.team) || {
            playsPerGame: 65,
            pointsPerGame: 22,
            redZoneScoreRate: 0.55,
            offensiveLineRank: 16,
          },
          leagueAverages: finalLeagueAverages,
          injuryHistory: basePlayer.injury_history || [],
        };
      });

      const rankingPlayers = playerArr.map((player) => ({
        ...player,
        projectedPoints: player.projection || 0,
        value: player.projection || 0,
        rank: player.adp || 999,
      }));

      let playerFeedback: Record<string, any> | null = null;
      try {
        playerFeedback = PlayerFeedbackStorage.getFeedbackForRankings();
      } catch {
        playerFeedback = null;
      }

      const enhancedConfig: EnhancedRankingConfig = {
        leagueConfig,
        currentRound: 1,
        userTeamNeeds: [],
        userRoster: [],
        playerFeedback,
      };

      const enhancedResult = calculateEnhancedRankings(rankingPlayers, enhancedConfig);
      const insightBoard = applyLeagueInsights(
        enhancedResult,
        leagueConfig.format.type,
        scoringFormat
      );

      let finalPlayers = insightBoard;
      if (scaleToTen) {
        const values = insightBoard.map((p) => p.value || 0).filter((v) => v > 0);
        if (values.length > 0) {
          const minValue = Math.min(...values);
          const maxValue = Math.max(...values);
          const range = maxValue - minValue;
          finalPlayers = insightBoard.map((player) => ({
            ...player,
            value: range > 0 ? ((player.value || 0) - minValue) / range * 10 : 0,
          }));
        }
      }

      if (finalPlayers.length === 0) {
        throw new Error('No fantasy-relevant players survived ranking filters.');
      }

      setPlayers(finalPlayers);
      setError(null);
    } catch (err: any) {
      console.error('Failed to fetch or process players:', err);
      setError(err.message || 'Could not load player data');
      setPlayers([]);
    } finally {
      setIsLoading(false);
    }
  }, [generatedProjections, leagueConfig, scaleToTen, scoringFormat, projectionsLoading]);

  useEffect(() => {
    fetchAndProcessPlayers();
  }, [fetchAndProcessPlayers]);

  return {
    players,
    isLoading,
    error,
  };
}
